import { useState, useEffect } from "react";
import useEmployeeDetails from "../hooks/useEmployeeDetails";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import moment from 'moment';

export default function AttendanceHistory() {
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);  // 🚨 Loading state
  const [errorMessage, setErrorMessage] = useState("");
  const { id, employeeToken } = useEmployeeDetails();
  const API_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    let isCancelled = false;

    const fetchAttendance = async () => {
      setLoading(true);  // 🚨 Start loading

      try {
        const payload = {
          user_id: id,
          month: month.toString(),
          year: year.toString(),
        };

        const response = await axios.post(`${API_URL}/staff_checkin_history`, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${employeeToken}`,
          },
        });

        const data = response.data;
        if (!isCancelled) {
          if (data.success === false) {
            setErrorMessage(data.message || "Something went wrong. Please try again.");
            setAttendanceData([]);
          } else {
            setAttendanceData(data.data ?? []);
            setErrorMessage("");
            setLoading(false)
          }
        }
      } catch (error) {
        console.error("API Error:", error);
        if (!isCancelled) {
          setErrorMessage("Server error. Please try again later.");
          setAttendanceData([]);
          navigate("/servererror");
          setLoading(false)
        }
      }
    };

    if (id && employeeToken && month && year) {
      fetchAttendance();
    }

    return () => {
      isCancelled = true;  // Cleanup flag
    };
  }, [id, employeeToken, API_URL, month, year, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 px-2 py-2">
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0,
          width: '100vw', height: '100vh',
          backgroundColor: 'rgba(200,200,200,0.8)',  // light gray with opacity
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 1000
        }}>
          <p style={{ fontSize: '1.5rem', color: '#333' }}>Loading...</p>
        </div>
      )}
      {/* DatePicker for month and year selection */}
      <div className="max-w-md mx-auto mb-4 flex gap-4">
        {/* Month Selector */}
        <select
          value={month}
          onChange={(e) => setMonth(parseInt(e.target.value))}
          className="w-full p-2 border rounded-md"
        >
          {[...Array(12)].map((_, i) => {
            const m = i + 1;
            const monthName = new Date(0, i).toLocaleString('en-US', { month: 'long' });
            return (
              <option key={m} value={m}>
                {monthName}
              </option>
            );
          })}
        </select>

        {/* Year Selector */}
        <select
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value))}
          className="w-full p-2 border rounded-md"
        >
          {/* For example, list years from 5 years ago to next year */}
          {[...Array(7)].map((_, i) => {
            const y = new Date().getFullYear() - 5 + i;
            return (
              <option key={y} value={y}>
                {y}
              </option>
            );
          })}
        </select>
      </div>


      {/* Error Message */}
      {errorMessage && (
        <div className="max-w-md mx-auto mb-4 p-3 bg-red-200 text-red-800 rounded-md">
          {errorMessage}
        </div>
      )}

      {/* Attendance Cards */}
      <div className="max-w-lg mx-auto space-y-4">
        {attendanceData.length === 0 && !errorMessage && (
          <p className="text-center text-gray-600">No attendance records found.</p>
        )}

        {attendanceData.map((entry, index) => {
          const checkinTime = moment.utc(entry.checkin_time_in_city_time_zone);
          const checkoutTime = moment.utc(entry.checkout_time_in_city_time_zone);
          const isCheckoutNull = !entry.checkout_time_in_city_time_zone || !checkoutTime.isValid();

          const endTime = isCheckoutNull ? moment.utc() : checkoutTime;
          const durationMs = endTime.diff(checkinTime);
          const duration = moment.duration(durationMs);

          const totalHours = Math.floor(duration.asHours());
          const minutes = duration.minutes();
          const days = Math.floor(totalHours / 24);
          const hours = totalHours % 24;

          let durationDisplay;
          if (durationMs <= 0) {
            durationDisplay = "--";
          } else if (days > 0) {
            durationDisplay = `${days}d ${hours}h ${minutes}m`;
          } else {
            durationDisplay = `${hours}h ${minutes}m`;
          }

          const punchIn = checkinTime.format('hh:mm A');
          const punchOut = isCheckoutNull ? '--' : checkoutTime.format('hh:mm A');

          let bgColor = "bg-red-500";

          if (isCheckoutNull) {
            bgColor = "bg-green-600"; // Still working, keep green
          } else if (totalHours >= 9) {
            // bgColor = "bg-[#6FAB55]";
            bgColor = "bg-green-600";
          } else if (totalHours >= 5) {
            bgColor = "bg-orange-500";
          } else {
            bgColor = "bg-red-500";
          }

          return (
            <div
              key={index}
              className="flex items-center bg-white rounded-lg shadow-md hover:shadow-lg px-4 py-2 transition w-[101%] overflow-hidden"
              style={{ gap: "1rem", minWidth: "320px" }}
            >
              {/* Date Block */}

              <div className={`flex flex-col items-center justify-center ${bgColor} text-white rounded-lg w-16 h-16 shadow-lg flex-shrink-0`}>
                <div className="text-2xl font-bold leading-tight">{checkinTime.local().date()}</div>
                <div className="text-sm font-medium">{checkinTime.local().format('ddd')}</div>
              </div>


              {/* Punch Details in One Row Only */}
              <div className="flex flex-nowrap items-center justify-center w-full">
                {/* Punch In */}
                <div className="flex flex-col items-start text-sm min-w-[80px] whitespace-nowrap truncate">
                  <p className="font-bold leading-tight truncate">{punchIn}</p>
                  <p className="text-xs text-gray-500">Punch In</p>
                </div>

                {/* Punch Out */}
                <div className="flex flex-col items-start text-sm min-w-[80px] whitespace-nowrap truncate">
                  <p className="font-bold leading-tight truncate">{punchOut}</p>
                  <p className="text-xs text-gray-500">Punch Out</p>
                </div>

                {/* Duration */}
                <div className="flex flex-col items-start text-[14px] min-w-[80px] whitespace-nowrap truncate">
                  <p className="font-bold leading-tight truncate">{durationDisplay}</p>
                  <p className="text-xs text-gray-500">Duration</p>
                </div>
              </div>
            </div>

          );
        })}
      </div>

    </div>
  );
}
